import { LanguageLink } from './LanguageLink';
import { useI18n } from '../i18n/i18n';
import { useLanguage } from './LanguageProvider';
import { useCurrentLanguage } from './LanguageRedirect';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { Instagram, Twitter, Linkedin, Lock } from 'lucide-react';

const Footer = () => {
  const { t } = useI18n();
  const { setLanguage, languageNames } = useLanguage();
  const currentLang = useCurrentLanguage(); // Get language from URL
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (newLang: string) => {
    // Save current scroll position
    const currentScrollY = window.scrollY;
    
    // Get the current path without the language prefix
    const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
    // Navigate to the new language URL
    const newPath = `/${newLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
    navigate(newPath);
    // Update the language state
    setLanguage(newLang as any);
    
    // Restore scroll position after navigation
    // Use setTimeout to wait for the page to render
    setTimeout(() => {
      window.scrollTo(0, currentScrollY);
    }, 0);
  };

  return (
    <footer className="bg-neutral-800 text-neutral-300 py-12">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Column 1: Social Media */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.connect')}</h4>
            <ul className="space-y-2">
              <li>
                <a href="https://instagram.com" className="hover:text-[#EA580C] transition-colors flex items-center" target="_blank" rel="noopener noreferrer">
                  <Instagram className="mr-2 h-4 w-4" /> Instagram
                </a>
              </li>
              <li>
                <a href="https://x.com/finanzly" className="hover:text-[#EA580C] transition-colors flex items-center" target="_blank" rel="noopener noreferrer">
                  <Twitter className="mr-2 h-4 w-4" /> Twitter
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/company/finanzly" className="hover:text-[#EA580C] transition-colors flex items-center" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="mr-2 h-4 w-4" /> LinkedIn
                </a>
              </li>
            </ul>
          </div>

          {/* Column 2: Tools */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.tools')}</h4>
            <ul className="space-y-2">
              <li>
                <LanguageLink to="/tools/compound-interest">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('tools.compound.title')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/tools/net-salary">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('tools.salary.title')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/tools/monthly-savings-calculator">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('tools.savings.title')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/tools/mortgage-calculator">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('tools.mortgage.title')}
                  </div>
                </LanguageLink>
              </li>
              <li className="text-neutral-500">
                {t('tools.portfolio.title')} ({t('footer.comingSoon')})
              </li>
            </ul>
          </div>

          {/* Column 3: Company */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2">
              <li>
                <LanguageLink to="/about">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('footer.about')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/resources">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('nav.resources')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/contact">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('footer.contact')}
                  </div>
                </LanguageLink>
              </li>
            </ul>
          </div>

          {/* Column 4: Legal */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              <li>
                <LanguageLink to="/terms">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('footer.terms')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/privacy">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('footer.privacy')}
                  </div>
                </LanguageLink>
              </li>
              <li>
                <LanguageLink to="/cookies">
                  <div className="hover:text-[#EA580C] transition-colors cursor-pointer">
                    {t('footer.cookies')}
                  </div>
                </LanguageLink>
              </li>
            </ul>
          </div>

          {/* Column 5: Language Selector */}
          <div>
            <h4 className="font-semibold text-white mb-4">{t('language.title')}</h4>
            <Select
              value={currentLang}
              onValueChange={handleLanguageChange}
            >
              <SelectTrigger className="bg-neutral-700 text-white border-neutral-600 focus:ring-[#EA580C] hover:bg-neutral-600 transition-colors" aria-label={t('language.title')}>
                <SelectValue>
                  {languageNames[currentLang]}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-neutral-700 border-neutral-600">
                {Object.entries(languageNames).map(([code, name]) => (
                  <SelectItem 
                    key={code} 
                    value={code}
                    className="text-white hover:bg-[#EA580C] hover:text-white focus:bg-[#EA580C] focus:text-white cursor-pointer"
                  >
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="mt-6">
              <div className="flex items-center space-x-2 mb-2">
                <img src="/logo.svg" alt="Finanzly" className="h-5 w-5" width="20" height="20" />
                <span className="text-white font-bold">Finanzly</span>
              </div>
              <p className="text-sm text-neutral-400">{t('app.tagline')}</p>
            </div>
          </div>
        </div>

        <div className="pt-8 mt-8 border-t border-neutral-700 text-center text-sm text-neutral-400">
          © Finanzly 2025 – {t('footer.allRightsReserved')}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
